'use client';

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { compareVersions } from '@/features/printing/domain/valueobject/printer-version';
import { printersServiceKeys } from '@/features/printing/presentation/queries/printers.queries';
import { PRINT_SERVICE_URL } from '@/features/printing/presentation/utils/print-tickets';

export const LATEST_PRINTER_VERSION = process.env.NEXT_PUBLIC_LATEST_PRINTER_VERSION || '1.4.0';

export interface PrinterServiceStatus {
  isHealthy: boolean;
  version: string | null;
  isOutdated: boolean;
  latestVersion: string;
  /** IP LAN de la máquina de caja (la reporta el driver desde `/health`; `null` en versiones viejas). */
  hostIp: string | null;
}

async function healthCheck(): Promise<PrinterServiceStatus> {
  try {
    const response = await fetch(`${PRINT_SERVICE_URL}/health`);

    if (!response.ok) {
      return {
        isHealthy: false,
        version: null,
        isOutdated: false,
        latestVersion: LATEST_PRINTER_VERSION,
        hostIp: null,
      };
    }

    const data = await response.json();
    const version: string | null = data.version ?? null;
    const hostIp: string | null = data.host_ip ?? data.hostIp ?? null;
    const isOutdated = version !== null && compareVersions(version, LATEST_PRINTER_VERSION) < 0;

    return {
      isHealthy: true,
      version,
      isOutdated,
      latestVersion: LATEST_PRINTER_VERSION,
      hostIp,
    };
  } catch (_error) {
    return {
      isHealthy: false,
      version: null,
      isOutdated: false,
      latestVersion: LATEST_PRINTER_VERSION,
      hostIp: null,
    };
  }
}

export function usePrintersService(): UseQueryResult<PrinterServiceStatus, Error> {
  return useQuery<PrinterServiceStatus, Error>({
    queryKey: printersServiceKeys.all,
    queryFn: healthCheck,
    refetchInterval: process.env.NODE_ENV === 'development' ? false : 1000 * 5,
    staleTime: process.env.NODE_ENV === 'development' ? undefined : 1000 * 5,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
